/**
 * Description: System view page displaying detailed system information and linked products
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Calendar, Copy, Package } from 'lucide-react'
import { api, System, Product } from '@services/api'
import { toast } from 'react-toastify'

const ViewSystem: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [system, setSystem] = useState<System | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadSystem()
    loadProducts()
  }, [id])

  const loadSystem = async () => {
    try {
      setIsLoading(true)
      const data = await api.getSystemById(parseInt(id!))
      setSystem(data)
    } catch (error) {
      toast.error('Failed to load system')
      console.error(error)
      navigate('/')
    } finally {
      setIsLoading(false)
    }
  }

  const loadProducts = async () => {
    try {
      const data = await api.getSystemProducts(parseInt(id!))
      setProducts(data)
    } catch (error) {
      console.error('Failed to load products:', error)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${system?.system_name}"?`)) {
      return
    }

    try {
      await api.deleteSystem(parseInt(id!))
      toast.success('System deleted successfully')
      navigate('/')
    } catch (error) {
      toast.error('Failed to delete system')
      console.error(error)
    }
  }

  const handleClone = async () => {
    if (!window.confirm(`Clone "${system?.system_name}"?`)) {
      return
    }

    try {
      const result = await api.cloneSystem(parseInt(id!))
      toast.success('System cloned successfully')
      navigate(`/systems/${result.id}/edit`)
    } catch (error) {
      toast.error('Failed to clone system')
      console.error(error)
    }
  }

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading system...</p>
      </div>
    )
  }

  if (!system) {
    return null
  }

  const InfoRow = ({ label, value }: { label: string; value?: string | boolean }) => {
    if (!value && value !== false) {
      return null
    }
    return (
      <div className="border-b border-gray-200 py-3 dark:border-gray-700">
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
        </dd>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/systems')}
            className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{system.system_name}</h2>
            {system.description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{system.description}</p>
            )}
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/systems/${id}/edit`)}
            className="btn-primary flex cursor-pointer items-center space-x-2"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
          <button
            onClick={handleClone}
            className="flex cursor-pointer items-center space-x-2 rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
          >
            <Copy className="h-4 w-4" />
            <span>Clone</span>
          </button>
          <button onClick={handleDelete} className="btn-danger flex cursor-pointer items-center space-x-2">
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {/* System Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Information */}
        <div className="space-y-6 lg:col-span-2">
          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">System Information</h3>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow label="Origin" value={system.origin} />
              <InfoRow label="Development Origin" value={system.acquisition_type} />
              <InfoRow label="Custom Type" value={system.custom_type} />
              <InfoRow label="User Interfaces" value={system.user_interfaces} />
              <InfoRow label="Maintenance Schedule" value={system.maintenance_schedule} />
            </dl>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Ownership & Management</h3>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow label="Business Owner" value={system.business_owner} />
              <InfoRow label="Technical Product Owner" value={system.technical_product_owner} />
              <InfoRow label="Management Type" value={system.management_type} />
            </dl>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Usage & Impact</h3>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow label="Customers Impacted" value={system.customers_impacted} />
              <InfoRow label="Monthly Volume" value={system.monthly_volume} />
              <InfoRow label="Customer Footprint" value={system.customer_footprint} />
              <InfoRow label="Products in System" value={system.product_related_notes} />
              <InfoRow label="Other User Groups" value={system.other_user_groups} />
              <InfoRow label="Site Capability" value={system.site_capability} />
              <InfoRow label="Geographic Scope" value={system.geographic_scope} />
            </dl>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Technical Details</h3>
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow label="Limitations" value={system.limitations} />
              <InfoRow label="Integration Capabilities" value={system.integration_capabilities} />
              <InfoRow label="Dependencies" value={system.dependencies} />
              <InfoRow label="Impacted Subsystems" value={system.impacted_subsystems} />
              <InfoRow label="NorthStar Alignment" value={system.northstar_alignment} />
            </dl>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Scores</h3>
            <div className="space-y-4">
              <div>
                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Security Score</p>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                    system.security_score === 'critical'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                      : system.security_score === 'high'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                        : system.security_score === 'medium'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  }`}
                >
                  {system.security_score || 'Not set'}
                </span>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Compliance Score</p>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                    system.compliance_score === 'critical'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                      : system.compliance_score === 'high'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                        : system.compliance_score === 'medium'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  }`}
                >
                  {system.compliance_score || 'Not set'}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Resources</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={system.shared_resources_non_cb || false}
                  readOnly
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <span className="text-gray-700 dark:text-gray-300">Shared with non-CB Comcast Organizations</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Metadata</h3>
            <div className="space-y-3 text-sm">
              {system.created_at && (
                <div className="flex items-start space-x-2">
                  <Calendar className="mt-0.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Created</p>
                    <p className="text-gray-900 dark:text-gray-100">{new Date(system.created_at).toLocaleString()}</p>
                    {system.created_by && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{system.created_by}</p>
                    )}
                  </div>
                </div>
              )}
              {system.updated_at && (
                <div className="flex items-start space-x-2">
                  <Calendar className="mt-0.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Updated</p>
                    <p className="text-gray-900 dark:text-gray-100">{new Date(system.updated_at).toLocaleString()}</p>
                    {system.updated_by && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{system.updated_by}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* System Complexity */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">System Complexity</h3>
          {system.complexity?.complexity_score !== undefined && system.complexity.complexity_score !== null && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Complexity Score:</span>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                  Number(system.complexity.complexity_score) >= 60
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    : Number(system.complexity.complexity_score) >= 30
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                      : Number(system.complexity.complexity_score) >= 10
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                }`}
              >
                {Number(system.complexity.complexity_score).toFixed(1)}
              </span>
            </div>
          )}
        </div>
        <dl className="divide-y divide-gray-200 dark:divide-gray-700">
          <InfoRow label="Code Owner" value={system.complexity?.code_owner} />
          <InfoRow label="API Documentation" value={system.complexity?.api_documentation} />
          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Code Access</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 dark:text-gray-100">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  system.complexity?.code_access
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {system.complexity?.code_access ? 'Available' : 'Not Available'}
              </span>
            </dd>
          </div>
          <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Code Owned</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0 dark:text-gray-100">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  system.complexity?.code_owned
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}
              >
                {system.complexity?.code_owned ? 'Owned by Comcast' : 'Not Owned'}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* Products Supported */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-gray-700 dark:text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Products Supported</h3>
            <span className="ml-2 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
              {products.length}
            </span>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="rounded-lg bg-gray-50 py-8 text-center dark:bg-white/5">
            <Package className="mx-auto mb-3 h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-500 dark:text-gray-400">No products linked to this system</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {products
              .sort((a, b) => a.product_name.localeCompare(b.product_name))
              .map((product) => (
                <div
                  key={product.id}
                  onClick={() => navigate(`/products/${product.id}`)}
                  className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-white/5 dark:hover:bg-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {product.product_name}
                      </p>
                      {product.description && (
                        <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                          {product.description}
                        </p>
                      )}
                      <div className="mt-2 flex gap-2">
                        {product.product_code && (
                          <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                            {product.product_code}
                          </span>
                        )}
                        {product.category && (
                          <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                            {product.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewSystem
